import React, { useEffect, useRef, useState } from "react";
import { RefreshCw, Loader2 } from "lucide-react";
import { useLang } from "@/contexts/LangContext";

/**
 * VersionRefreshToast
 *
 * Detects when a new service worker is `waiting` and shows a subtle
 * "new version available — tap to refresh" pill. Tapping it messages
 * the SW to skipWaiting, then reloads the page once the new SW takes
 * control (via the `controllerchange` event).
 *
 * Rationale: guests won't ever refresh the tab of a PWA on their own.
 * Without this, they run stale code for days after every deploy.
 */

const COPY = {
  en: {
    title: "A fresh version just landed",
    body: "Andreas polished a few things — new fixes, new details. Tap Refresh to get the latest.",
    cta: "Refresh now",
  },
  fr: {
    title: "Une nouvelle version est arrivée",
    body: "Andreas a peaufiné quelques trucs — corrections, détails. Touche Rafraîchir pour tout récupérer.",
    cta: "Rafraîchir maintenant",
  },
  el: {
    title: "Έφτασε νέα έκδοση",
    body: "Ο Andreas γυάλισε μερικά πράγματα — διορθώσεις και νέες λεπτομέρειες. Πάτα Ανανέωση για τα τελευταία.",
    cta: "Ανανέωση τώρα",
  },
  pl: {
    title: "Jest nowa wersja",
    body: "Andreas dopieścił parę rzeczy — poprawki, nowe detale. Kliknij Odśwież, żeby je zobaczyć.",
    cta: "Odśwież teraz",
  },
};

const VersionRefreshToast = () => {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;
  const [waitingReg, setWaitingReg] = useState(null);
  const [reloading, setReloading] = useState(false);
  const reloadedRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
    let mounted = true;

    const setup = async () => {
      let reg;
      try {
        reg = await navigator.serviceWorker.getRegistration("/service-worker.js");
      } catch { return; }
      if (!reg || !mounted) return;

      const setIfWaiting = () => {
        if (reg.waiting && !reloadedRef.current) setWaitingReg(reg);
      };
      // Case 1: an update is already waiting when this component mounts
      setIfWaiting();

      // Case 2: an update lands while the app is open
      reg.addEventListener("updatefound", () => {
        const nw = reg.installing;
        if (!nw) return;
        nw.addEventListener("statechange", () => {
          if (nw.state === "installed" && navigator.serviceWorker.controller) {
            setIfWaiting();
          }
        });
      });

      // Periodically poke the server for a fresh SW while the tab is open
      // (browsers only auto-check every 24h). One check per 15 minutes.
      const poll = setInterval(() => {
        reg.update().catch(() => {});
      }, 15 * 60 * 1000);

      // When the new SW takes control (after we ask it to SKIP_WAITING),
      // reload the page so the guest actually runs the new code.
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (reloadedRef.current) return;
        reloadedRef.current = true;
        window.location.reload();
      });

      return () => { clearInterval(poll); };
    };

    const cleanup = setup();
    return () => {
      mounted = false;
      Promise.resolve(cleanup).then((fn) => typeof fn === "function" && fn());
    };
  }, []);

  const onRefresh = () => {
    if (!waitingReg || !waitingReg.waiting || reloading) return;
    setReloading(true);
    try {
      waitingReg.waiting.postMessage("SKIP_WAITING");
    } catch {
      // Fallback — just reload
      window.location.reload();
    }
  };

  const onDismiss = () => setWaitingReg(null);

  if (!waitingReg) return null;

  return (
    <div
      className="fixed inset-0 z-[400] flex items-center justify-center p-4 bg-foreground/60 backdrop-blur-md animate-in fade-in-0 duration-200"
      role="dialog"
      aria-modal="true"
      aria-labelledby="version-refresh-title"
      data-testid="version-refresh-toast"
    >
      <div className="relative w-full max-w-md bg-background border border-accent/30 rounded-2xl shadow-2xl overflow-hidden animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-6 duration-300">
        {/* Decorative gradient stripe */}
        <div className="h-1 w-full bg-gradient-to-r from-accent/60 via-accent to-accent/60" aria-hidden="true" />

        <div className="p-6 sm:p-7">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 shrink-0 rounded-full bg-accent/15 text-accent inline-flex items-center justify-center">
              <RefreshCw className={"w-6 h-6 " + (reloading ? "animate-spin" : "")} aria-hidden="true" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="editorial-kicker text-accent">Andreas has news</p>
              <h2
                id="version-refresh-title"
                className="font-serif-display text-2xl sm:text-[28px] leading-tight mt-1"
                data-testid="version-refresh-title"
              >
                {c.title}
              </h2>
              <p className="text-[14px] sm:text-[15px] leading-relaxed text-foreground/75 mt-3">
                {c.body}
              </p>
            </div>
          </div>

          <div className="mt-7 flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={onDismiss}
              disabled={reloading}
              className="h-11 px-4 text-[11px] tracking-[0.22em] uppercase text-foreground/60 hover:text-foreground disabled:opacity-50"
              data-testid="version-refresh-dismiss"
            >
              Later
            </button>
            <button
              type="button"
              onClick={onRefresh}
              disabled={reloading}
              className="flex-1 h-12 px-6 rounded-xl bg-foreground text-background hover:bg-foreground/90 disabled:opacity-60 text-[12px] tracking-[0.22em] uppercase inline-flex items-center justify-center gap-2"
              data-testid="version-refresh-cta"
            >
              {reloading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Refreshing…
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  {c.cta}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VersionRefreshToast;
